// src/pages/krebsgesamt/rs_entit2.js
import React, { useMemo, useState, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ErrorBar,
} from "recharts";
import { Container, Row, Col, Form, Table } from "react-bootstrap";
import html2canvas from "html2canvas";
import data from "../../assets/data/RS_SEX_STADIUM.json";
import { DATENSTAND } from "../../config/constants";
import { exportToExcel } from "../../utils/exportExcel";
import { SEX_COLORS, SURVIVAL_STAGE_COLORS } from "../../config/colors";



const stageKeys = ["stage0", "stageI", "stageII", "stageIII", "stageIV", "stageX"];
const stageLabel = {
  stage0: "0",
  stageI: "I",
  stageII: "II",
  stageIII: "III",
  stageIV: "IV",
  stageX: "X",
};

const EXCLUDE_STAGES_FOR = [
  "Krebs gesamt ohne C44",
  "Chronische lymphatische Leukämie (CLL/SLL)",
  "Multiples Myelom",
  "Diffuses großzelliges B-Zell-Lymphom (DLBCL)",
];

// keep only years up to 5 (and keep possible nulls)
const clean = (rows) => rows.filter((r) => r.year == null || r.year <= 5);

const formatNumber = (num) =>
  num != null && Number.isFinite(num)
    ? String(num.toFixed(1)).replace(".", ",")
    : "–";

const extractEntitiesSorted = (rows) => {
  const set = new Set();
  rows.forEach((d) => set.add(d.group.split("_")[0]));
  return [...set].sort((a, b) => a.localeCompare(b, "de"));
};

// Build +/- arrays for ErrorBar (for sex-specific curves)
const addRelCI = (arr) =>
  arr.map((d) => ({
    ...d,
    rel_ci:
      d.rel_surv != null && d.rel_lower != null && d.rel_upper != null
        ? [d.rel_surv - d.rel_lower, d.rel_upper - d.rel_surv]
        : [0, 0],
  }));

// helper: which sex is allowed for an entity?
// "M" -> only men, "W" -> only women, null -> both
const getAllowedSexForEntit = (entit) => {
  const name = (entit || "").toLowerCase();

  // only men
  if (name.includes("prostata")) {
    return "M";
  }

  // only women
  if (name.includes("gebärmutterkörper")) {
    return "W";
  }

  // otherwise both sexes
  return null;
};

// Compact legend for sex chart
const StyleLegend = () => (
  <div style={{ fontSize: 12, color: "#555", margin: "6px 0 10px" }}>
    <span
      style={{
        display: "inline-block",
        width: 28,
        height: 0,
        borderTop: `2px solid ${SEX_COLORS.M}`,
        marginRight: 6,
        verticalAlign: "middle",
      }}
    />
    Männer relativ
    <span style={{ display: "inline-block", width: 12 }} />
    <span
      style={{
        display: "inline-block",
        width: 28,
        height: 0,
        borderTop: `2px dashed ${SEX_COLORS.M}`,
        marginRight: 6,
        verticalAlign: "middle",
      }}
    />
    Männer absolut
    <span style={{ display: "inline-block", width: 18 }} />
    <span
      style={{
        display: "inline-block",
        width: 28,
        height: 0,
        borderTop: `2px solid ${SEX_COLORS.W}`,
        marginRight: 6,
        verticalAlign: "middle",
      }}
    />
    Frauen relativ
    <span style={{ display: "inline-block", width: 12 }} />
    <span
      style={{
        display: "inline-block",
        width: 28,
        height: 0,
        borderTop: `2px dashed ${SEX_COLORS.W}`,
        marginRight: 6,
        verticalAlign: "middle",
      }}
    />
    Frauen absolut
  </div>
);

// Compact legend for stage chart
const StageLegend = ({ keys }) => (
  <div style={{ fontSize: 12, color: "#555", margin: "6px 0 10px" }}>
    {keys.map((k) => {
      const color = SURVIVAL_STAGE_COLORS[stageLabel[k]];

      return (
        <span key={k} style={{ marginRight: 16 }}>
          <span
            style={{
              display: "inline-block",
              width: 28,
              height: 0,
              borderTop: `2px solid ${color}`,
              marginRight: 6,
              verticalAlign: "middle",
            }}
          />
          {`Stadium ${stageLabel[k]}`}
        </span>
      );
    })}
  </div>
);

// Shared tooltip, different behavior for sex vs stage chart
const CustomTooltip = ({
  active,
  payload,
  label,
  context,
  male = [],
  female = [],
  allowedSex,
}) => {
  if (!active) return null;

  // Sex chart: always show both sexes for the given year, if available
  if (context === "sex") {
    const year = Number(label);

    const m = male.find((r) => r.year === year);
    const f = female.find((r) => r.year === year);

    const showMale = allowedSex !== "W" && m;
    const showFemale = allowedSex !== "M" && f;

    if (!showMale && !showFemale) return null;

    return (
      <div className="bg-white p-2 border rounded shadow-sm">
        <strong>Jahr {label}</strong>

        {showMale && (
          <div style={{ marginTop: 6, color: SEX_COLORS.M }}>
            <div style={{ fontWeight: 600 }}>Männer</div>
            {m.rel_surv != null && (
              <div>
                Relativ: {formatNumber(m.rel_surv)}%{" "}
                <span style={{ color: "#666" }}>
                  ({formatNumber(m.rel_lower)} – {formatNumber(m.rel_upper)})
                </span>
              </div>
            )}
            {m.abs_surv != null && (
              <div>
                Absolut: {formatNumber(m.abs_surv)}%{" "}
                <span style={{ color: "#666" }}>
                  ({formatNumber(m.abs_lower)} – {formatNumber(m.abs_upper)})
                </span>
              </div>
            )}
          </div>
        )}

        {showFemale && (
          <div style={{ marginTop: 6, color: SEX_COLORS.W }}>
            <div style={{ fontWeight: 600 }}>Frauen</div>
            {f.rel_surv != null && (
              <div>
                Relativ: {formatNumber(f.rel_surv)}%{" "}
                <span style={{ color: "#666" }}>
                  ({formatNumber(f.rel_lower)} – {formatNumber(f.rel_upper)})
                </span>
              </div>
            )}
            {f.abs_surv != null && (
              <div>
                Absolut: {formatNumber(f.abs_surv)}%{" "}
                <span style={{ color: "#666" }}>
                  ({formatNumber(f.abs_lower)} – {formatNumber(f.abs_upper)})
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Stage chart: use payload from Recharts as before
  if (!payload || payload.length === 0) return null;

  const stages = [];

  payload.forEach((p) => {
    const key = p.dataKey;
    if (!key) return;
    const v = p.payload?.[key];
    const lo = p.payload?.[`${key}_lo`];
    const hi = p.payload?.[`${key}_hi`];
    if (v != null) {
      stages.push({
        label: p.name,
        v,
        lo,
        hi,
        color: p.stroke,
      });
    }
  });

  if (!stages.length) return null;

  return (
    <div className="bg-white p-2 border rounded shadow-sm">
      <strong>Jahr {label}</strong>
      {stages.map((s) => (
        <div key={s.label} style={{ marginTop: 6, color: s.color }}>
          {s.label}: {formatNumber(s.v)}%{" "}
          <span style={{ color: "#666" }}>
            ({formatNumber(s.lo)} – {formatNumber(s.hi)})
          </span>
        </div>
      ))}
    </div>
  );
};

const RelativeSurvival2 = () => {
  // entities list with "Krebs gesamt ohne C44" on top
  const entities = useMemo(() => {
    const raw = extractEntitiesSorted(data);
    const special = "Krebs gesamt ohne C44";
    return raw.includes(special)
      ? [special, ...raw.filter((e) => e !== special)]
      : raw;
  }, []);

  const [selectedEntity, setSelectedEntity] = useState(entities[0]);

  const isExcludedEntity = EXCLUDE_STAGES_FOR.includes(selectedEntity);

  const allowedSex = useMemo(
    () => getAllowedSexForEntit(selectedEntity),
    [selectedEntity]
  );

  // Keep original graph behavior (year 0 included)
  const filtered = useMemo(
    () => data.filter((d) => d.group.startsWith(selectedEntity)),
    [selectedEntity]
  );

  // apply sex filter: for sex-specific cancers, hide the other sex completely
  const male = useMemo(() => {
    if (allowedSex === "W") return []; // hide men
    return clean(filtered.filter((d) => d.group.includes("_sex1")));
  }, [filtered, allowedSex]);

  const female = useMemo(() => {
    if (allowedSex === "M") return []; // hide women
    return clean(filtered.filter((d) => d.group.includes("_sex2")));
  }, [filtered, allowedSex]);

  const stages = useMemo(() => {
    const acc = {};
    filtered
      .filter((d) => d.group.includes("_stage"))
      .forEach((d) => {
        const stageKey = d.group.split("_")[1]; // e.g. "stageI"
        if (!acc[stageKey]) acc[stageKey] = [];
        acc[stageKey].push(d);
      });

    Object.keys(acc).forEach((k) => {
      acc[k] = addRelCI(clean(acc[k]).sort((a, b) => a.year - b.year));
    });

    return acc;
  }, [filtered]);

  // Only use stages that actually have data for this entity
  const activeStageKeys = useMemo(
    () => stageKeys.filter((k) => stages[k] && stages[k].length),
    [stages]
  );

  const maleWithErrors = useMemo(() => addRelCI(male), [male]);
  const femaleWithErrors = useMemo(() => addRelCI(female), [female]);

  // Tables (exclude year 0 only here)
  const years = [1, 2, 3, 4, 5];

  const sexRows = useMemo(
    () =>
      years.map((y) => {
        const m = male.find((r) => r.year === y);
        const f = female.find((r) => r.year === y);

        let row = {
          year: y,
          mRel: m?.rel_surv ?? null,
          mRelLo: m?.rel_lower ?? null,
          mRelHi: m?.rel_upper ?? null,
          mAbs: m?.abs_surv ?? null,
          mAbsLo: m?.abs_lower ?? null,
          mAbsHi: m?.abs_upper ?? null,
          fRel: f?.rel_surv ?? null,
          fRelLo: f?.rel_lower ?? null,
          fRelHi: f?.rel_upper ?? null,
          fAbs: f?.abs_surv ?? null,
          fAbsLo: f?.abs_lower ?? null,
          fAbsHi: f?.abs_upper ?? null,
        };

        // enforce sex-specific hiding in the table/export as well
        if (allowedSex === "M") {
          row = {
            ...row,
            fRel: null,
            fRelLo: null,
            fRelHi: null,
            fAbs: null,
            fAbsLo: null,
            fAbsHi: null,
          };
        } else if (allowedSex === "W") {
          row = {
            ...row,
            mRel: null,
            mRelLo: null,
            mRelHi: null,
            mAbs: null,
            mAbsLo: null,
            mAbsHi: null,
          };
        }

        return row;
      }),
    [years, male, female, allowedSex]
  );

  // Stage rows for tables (years 1–5)
  const stageRows = useMemo(() => {
    return years.map((y) => {
      const row = { year: y };
      stageKeys.forEach((k) => {
        const rec = (stages[k] || []).find((r) => r.year === y);
        row[k] = rec?.rel_surv ?? null;
        row[`${k}_lo`] = rec?.rel_lower ?? null;
        row[`${k}_hi`] = rec?.rel_upper ?? null;
      });
      return row;
    });
  }, [stages]);

  // Stage rows for chart (years 0–5, single shared dataset)
  const stageRowsChart = useMemo(() => {
    const chartYears = [0, 1, 2, 3, 4, 5];
    return chartYears.map((y) => {
      const row = { year: y };
      stageKeys.forEach((k) => {
        const rec = (stages[k] || []).find((r) => r.year === y);
        const v = rec?.rel_surv ?? null;
        const lo = rec?.rel_lower ?? null;
        const hi = rec?.rel_upper ?? null;
        row[k] = v;
        row[`${k}_lo`] = lo;
        row[`${k}_hi`] = hi;
        row[`${k}_ci`] =
          v != null && lo != null && hi != null
            ? [v - lo, hi - v]
            : [0, 0];
      });
      return row;
    });
  }, [stages]);

  const handleExport = () => {
    // First table (sex)
    const sexSheet = sexRows.map((r) => ({
      Zeitraum: `${r.year}-Jahres-Überleben`,
      "Männer relativ": formatNumber(r.mRel),
      "Männer rel (CI)": `${formatNumber(r.mRelLo)} – ${formatNumber(
        r.mRelHi
      )}`,
      "Männer absolut": formatNumber(r.mAbs),
      "Männer abs (CI)": `${formatNumber(r.mAbsLo)} – ${formatNumber(
        r.mAbsHi
      )}`,
      "Frauen relativ": formatNumber(r.fRel),
      "Frauen rel (CI)": `${formatNumber(r.fRelLo)} – ${formatNumber(
        r.fRelHi
      )}`,
      "Frauen absolut": formatNumber(r.fAbs),
      "Frauen abs (CI)": `${formatNumber(r.fAbsLo)} – ${formatNumber(
        r.fAbsHi
      )}`,
    }));

    // Second table (stages) only if not excluded
    const stageSheet = isExcludedEntity
      ? []
      : stageRows.map((r) => {
          const obj = { Zeitraum: `${r.year}-Jahres-Überleben` };
          activeStageKeys.forEach((k) => {
            obj[`Stadium ${stageLabel[k]}`] = formatNumber(r[k]);
            obj[`CI ${stageLabel[k]}`] = `${formatNumber(
              r[`${k}_lo`]
            )} – ${formatNumber(r[`${k}_hi`] )}`;
          });
          return obj;
        });

    // Excel: both tables in one sheet (stacked with an empty row between if we have stage data)
    const excelData =
      stageSheet.length > 0 ? [...sexSheet, {}, ...stageSheet] : [...sexSheet];

    exportToExcel({
      filename: "ueberleben",
      sheetName: "Überleben",
      data: excelData,
      metadata: [
        ["Überleben"],
        ["Krebsregister Sachsen-Anhalt https://www.kr-lsa.de"],
        [`Datenstand: ${DATENSTAND}`],
        [`Export am: ${new Date().toLocaleString("de-DE")}`],
      ],
    });
  };

  // --- refs for HTML-to-image exports ---
  const sexChartRef = useRef(null);
  const stageChartRef = useRef(null);

  const sanitizeForFilename = (s) =>
    String(s)
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9äöüß_-]/gi, "");

  const handleDownloadSexChart = async () => {
    if (!sexChartRef.current) return;
    const canvas = await html2canvas(sexChartRef.current, {
      scale: 3,
      backgroundColor: "#ffffff",
    });
    const link = document.createElement("a");
    const safeEnt = sanitizeForFilename(selectedEntity);
    link.href = canvas.toDataURL("image/png");
    link.download = `5jahres_relatives_ueberleben_geschlecht_${safeEnt}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadStageChart = async () => {
    if (isExcludedEntity || !stageChartRef.current) return;
    const canvas = await html2canvas(stageChartRef.current, {
      scale: 3,
      backgroundColor: "#ffffff",
    });
    const link = document.createElement("a");
    const safeEnt = sanitizeForFilename(selectedEntity);
    link.href = canvas.toDataURL("image/png");
    link.download = `5jahres_relatives_ueberleben_stadium_${safeEnt}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container className="mt-4">
      <h2 className="section-title">Überleben</h2>
      <div className="section-title-underline"></div>
      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Bessere Therapiemöglichkeiten sowie der Einsatz von Früherkennungsmaßnahmen (z.B. das Mammographiescreening)
          haben dazu beigetragen, dass sich die Überlebenszeiten nahezu aller Krebserkrankungen in den letzten Jahrzehnten verbessert haben.
          Entscheidende Faktoren für die Prognose sind neben der Lokalisation der Krebserkrankung der histologische Typ sowie das Stadium der Erkrankung.
        </p>
        <p>
          Unten stehend sehen Sie die absolute und relative Überlebenswahrscheinlichkeit der häufigsten Krebsarten für den Periodenzeitraum 2019-2023.
        </p>
      </div>

      <button onClick={handleExport} className="top-btn mb-3">
        📥 Daten als Excel exportieren
      </button>

      <Form.Group className="mb-3" controlId="cancerSelect">
        <Form.Label>Krebs-Auswahl:</Form.Label>
        <Form.Select
          value={selectedEntity}
          onChange={(e) => setSelectedEntity(e.target.value)}
        >
          {entities.map((ent) => (
            <option key={ent} value={ent}>
              {ent}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Row>
        {/* Sex chart */}
        <Col md={6}>
          <div ref={sexChartRef}>
            <h5 className="mb-1">Überlebensrate nach Geschlecht</h5>
            <div
              style={{
                fontSize: 12,
                color: "#555",
                marginBottom: 8,
              }}
            >
              5-Jahres relative Überlebensraten für {selectedEntity}
            </div>
            <StyleLegend />
            <ResponsiveContainer width="100%" height={500}>
              <LineChart margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="year"
                  type="number"
                  domain={[0, 5]} // keep year 0 visible
                  ticks={[0, 1, 2, 3, 4, 5]}
                  label={{
                    value: "Jahr",
                    position: "insideBottom",
                    offset: -5,
                  }}
                />
                <YAxis
                  domain={[0, 105]}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  tickFormatter={(v) => `${v}%`}
                  label={{
                    value: "Überleben (%)",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip
                  content={(props) => (
                    <CustomTooltip
                      {...props}
                      context="sex"
                      male={male}
                      female={female}
                      allowedSex={allowedSex}
                    />
                  )}
                />

                <Line
                  data={maleWithErrors}
                  dataKey="rel_surv"
                  name="Männer relativ"
                  stroke={SEX_COLORS.M}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                >
                  <ErrorBar
                    dataKey="rel_ci"
                    width={4}
                    strokeWidth={1}
                    stroke={SEX_COLORS.M}
                    direction="y"
                  />
                </Line>
                <Line
                  data={male}
                  dataKey="abs_surv"
                  name="Männer absolut"
                  stroke={SEX_COLORS.M}
                  strokeDasharray="4 4"
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  data={femaleWithErrors}
                  dataKey="rel_surv"
                  name="Frauen relativ"
                  stroke={SEX_COLORS.W}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                >
                  <ErrorBar
                    dataKey="rel_ci"
                    width={4}
                    strokeWidth={1}
                    stroke={SEX_COLORS.W}
                    direction="y"
                  />
                </Line>
                <Line
                  data={female}
                  dataKey="abs_surv"
                  name="Frauen absolut"
                  stroke={SEX_COLORS.W}
                  strokeDasharray="4 4"
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Col>

        {/* Stage chart */}
        <Col md={6}>
          <div ref={stageChartRef}>
            <h5 className="mb-1">Relative Überlebensrate nach Stadium</h5>
            <div
              style={{
                fontSize: 12,
                color: "#555",
                marginBottom: 8,
              }}
            >
              5-Jahres relative Überlebensraten für {selectedEntity}
            </div>
            {isExcludedEntity ? (
              <p
                className="text-muted"
                style={{ marginTop: 4, fontSize: 14 }}
              >
                Für diese Entität liegen keine stadienbezogenen Überlebensdaten vor.
              </p>
            ) : (
              <>
                <StageLegend keys={activeStageKeys} />
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart
                    data={stageRowsChart}
                    margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="year"
                      type="number"
                      domain={[0, 5]} // keep year 0 visible
                      ticks={[0, 1, 2, 3, 4, 5]}
                      label={{
                        value: "Jahr",
                        position: "insideBottom",
                        offset: -5,
                      }}
                    />
                    <YAxis
                      domain={[0, 105]}
                      ticks={[0, 20, 40, 60, 80, 100]}
                      tickFormatter={(v) => `${v}%`}
                      label={{
                        value: "Überleben (%)",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip
                      content={(props) => (
                        <CustomTooltip
                          {...props}
                          context="stage"
                        />
                      )}
                    />
                    {activeStageKeys.map((k) => {
                        const color = SURVIVAL_STAGE_COLORS[stageLabel[k]];

                        return (
                          <Line
                            key={k}
                            dataKey={k}
                            name={`Stadium ${stageLabel[k]}`}
                            stroke={color}
                            strokeWidth={2.5}
                            dot={false}
                            isAnimationActive={false}
                          >
                            <ErrorBar
                              dataKey={`${k}_ci`}
                              width={4}
                              strokeWidth={1}
                              stroke={color}
                              direction="y"
                            />
                          </Line>
                        );
                      })}
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </div>
        </Col>
      </Row>

      {/* Tables */}
      <Row className="mt-4 g-4">
        {/* Sex table (left) */}
        <Col md={6}>
          <h5>Geschlecht – Überlebensraten</h5>
          <Table
            responsive
            className="zeitverlauf-table text-center align-middle"
            style={{ maxWidth: "1000px" }}
          >
            <thead>
              <tr>
                <th className="text-start">Zeitraum</th>
                <th>Männer (relativ)</th>
                <th>Frauen (relativ)</th>
              </tr>
            </thead>
            <tbody>
              {sexRows.map((r) => (
                <tr key={`sex-${r.year}`}>
                  <td className="text-start">
                    {r.year}-Jahres-Überleben (alterstd)
                  </td>
                  <td>
                    {formatNumber(r.mRel)}{" "}
                    <span style={{ color: "#666" }}>
                      ({formatNumber(r.mRelLo)} –{" "}
                      {formatNumber(r.mRelHi)})
                    </span>
                    <div style={{ fontSize: 12, color: "#777" }}>
                      abs.: {formatNumber(r.mAbs)}{" "}
                      <span>
                        ({formatNumber(r.mAbsLo)} –{" "}
                        {formatNumber(r.mAbsHi)})
                      </span>
                    </div>
                  </td>
                  <td>
                    {formatNumber(r.fRel)}{" "}
                    <span style={{ color: "#666" }}>
                      ({formatNumber(r.fRelLo)} –{" "}
                      {formatNumber(r.fRelHi)})
                    </span>
                    <div style={{ fontSize: 12, color: "#777" }}>
                      abs.: {formatNumber(r.fAbs)}{" "}
                      <span>
                        ({formatNumber(r.fAbsLo)} –{" "}
                        {formatNumber(r.fAbsHi)})
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>

        {/* Stage table (right) */}
        <Col md={6}>
          <h5>Stadium – Relative Überlebensraten</h5>
          <Table
            responsive
            className="zeitverlauf-table text-center align-middle"
            style={{ maxWidth: "1000px" }}
          >
            <thead>
              <tr>
                <th className="text-start">Zeitraum</th>
                {!isExcludedEntity &&
                  activeStageKeys.map((k) => (
                    <th key={k}>{`Stadium ${stageLabel[k]}`}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {isExcludedEntity ? (
                <tr>
                  <td colSpan={1} className="text-start">
                    Für diese Entität liegen keine stadienbezogenen Überlebensdaten vor.
                  </td>
                </tr>
              ) : (
                stageRows.map((r) => (
                  <tr key={`stage-${r.year}`}>
                    <td className="text-start">
                      {r.year}-Jahres-Überleben (alterstd)
                    </td>
                    {activeStageKeys.map((k) => (
                      <td key={k}>
                        {formatNumber(r[k])}{" "}
                        <span style={{ color: "#666" }}>
                          ({formatNumber(r[`${k}_lo`])} –{" "}
                          {formatNumber(r[`${k}_hi`])})
                        </span>
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
};

export default RelativeSurvival2;

